import React, { useState } from "react";
import { MenuIcon, CloseIcon, ClientIcon } from "@/components/icons";
import { BRAND_GRADIENT_TRACK } from "@/components/ui/brandGradient";
import { useLocale } from "@/hooks/useLocale";

export interface ClientMenuItem {
  label: string;
  onSelect: () => void;
}

interface ApprovalHeaderProps {
  clientName: string;
  onLogout?: () => void;
  /** Pages with no place in the shell's top-level nav (DD-025). */
  menuItems?: ClientMenuItem[];
}

/** Full-screen burger menu: pages that have no top-level tab, then the
 * logout pinned at the bottom (DD-025). */
const ClientMenu: React.FC<{
  items: ClientMenuItem[];
  onClose: () => void;
  onLogout: () => void;
}> = ({ items, onClose, onLogout }) => {
  const { t } = useLocale();
  return (
    <div className="fixed inset-0 z-50 bg-gray-50 flex flex-col">
      <div className="max-w-2xl mx-auto w-full px-4 lg:px-6 py-3 flex items-center justify-end">
        <button
          type="button"
          onClick={onClose}
          aria-label={t("client.menu.close")}
          className="p-2 -mr-2 text-gray-600 hover:text-gray-900 transition-colors"
        >
          <CloseIcon className="w-6 h-6" />
        </button>
      </div>
      <nav className="max-w-2xl mx-auto w-full px-4 lg:px-6">
        {items.map((item) => (
          <button
            key={item.label}
            type="button"
            onClick={() => {
              onClose();
              item.onSelect();
            }}
            className="w-full text-left px-3 py-3 rounded-lg text-lg font-semibold text-gray-900 hover:bg-gray-100 transition-colors"
          >
            {item.label}
          </button>
        ))}
      </nav>
      <div className="flex-1" />
      <div className="max-w-2xl mx-auto w-full px-4 lg:px-6 pb-8">
        <button
          type="button"
          onClick={onLogout}
          className="w-full text-left px-3 py-3 rounded-lg text-lg font-semibold text-red-600 hover:bg-red-50 transition-colors"
        >
          {t("client.header.logout")}
        </button>
      </div>
    </div>
  );
};

export const ApprovalHeader: React.FC<ApprovalHeaderProps> = ({
  clientName,
  onLogout,
  menuItems = [],
}) => {
  const { t } = useLocale();
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    // Transparent header, no divider (DD-011/DD-025). Burger on the left,
    // workspace identity as a static gradient pill on the right.
    <div className="flex items-center justify-between w-full gap-3">
      <button
        type="button"
        onClick={() => setMenuOpen(true)}
        aria-label={t("client.menu.open")}
        className="p-2 -ml-2 text-gray-600 hover:text-gray-900 transition-colors shrink-0"
      >
        <MenuIcon className="w-5 h-5" />
      </button>

      <div
        className={`flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-900 ${BRAND_GRADIENT_TRACK} rounded-lg min-w-0`}
      >
        <ClientIcon className="w-4 h-4 text-gray-500 shrink-0" />
        <span className="truncate">{clientName}</span>
      </div>

      {menuOpen && onLogout && (
        <ClientMenu
          items={menuItems}
          onClose={() => setMenuOpen(false)}
          onLogout={onLogout}
        />
      )}
    </div>
  );
};
